import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { LoginData } from '../services/auth.service';

const Login: React.FC = () => {
  const [loginData, setLoginData] = useState<LoginData>({ 
    email: '', 
    method: 'email',
    password: '' 
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { showError, showSuccess } = useToast();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Show loading if auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't show login if already authenticated
  if (isAuthenticated) {
    return null;
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    
    try {
      console.log('Attempting login with:', loginData);
      await login(loginData);
      console.log('Login successful');
      
      showSuccess('Login Successful', 'Welcome back! Redirecting to dashboard...');
      
      // Small delay to show success message
      setTimeout(() => {
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }, 1000);
      
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error?.message || 
                          err.response?.data?.message || 
                          err.message || 
                          'Invalid credentials';
      showError('Login Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white">
          <div className="space-y-8">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-2xl">🚛</span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full animate-ping"></div>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                  TruckTrack
                </h1>
                <p className="text-blue-200 text-sm">Fleet Management Platform</p>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-bold leading-tight">
                Manage Your Fleet
                <span className="block text-3xl text-blue-300">Like Never Before</span>
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed">
                Streamline operations, track deliveries, and optimize your truck fleet with our comprehensive management platform.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="w-8 h-8 bg-emerald-400 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-white text-sm">📊</span>
                </div>
                <h3 className="font-semibold text-white mb-1">Real-time Analytics</h3>
                <p className="text-blue-200 text-sm">Track performance metrics</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="w-8 h-8 bg-blue-400 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-white text-sm">🚚</span>
                </div>
                <h3 className="font-semibold text-white mb-1">Fleet Management</h3>
                <p className="text-blue-200 text-sm">Optimize your operations</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">AH</span>
              </div>
              <div>
                <p className="text-white font-medium">Ahmed Hassan</p>
                <p className="text-blue-200 text-sm">Fleet Manager, Premium Logistics</p>
              </div>
        </div>
            <blockquote className="text-blue-100 italic">
              "TruckTrack has revolutionized our fleet management. The real-time tracking and analytics have improved our efficiency by 40%."
          </blockquote>
        </div>
      </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-xl">🚛</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">TruckTrack</h1>
                  <p className="text-blue-200 text-sm">Fleet Management</p>
                </div>
              </div>
            </div>

            {/* Login Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
                <p className="text-blue-200">
              {location.state?.from 
                ? `Please login to access ${location.state.from.pathname}`
                    : 'Sign in to your account to continue'
              }
            </p>
          </div>

          {/* Test Credentials */}
              {/* <div className="mb-6 p-4 bg-blue-500/20 backdrop-blur-sm rounded-xl border border-blue-400/30">
                <div className="flex items-center mb-3">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white text-xs">💡</span>
                  </div>
                  <p className="font-semibold text-blue-100">Test Credentials</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Admin:</span>
                    <code className="text-blue-100 bg-blue-600/30 px-2 py-1 rounded text-xs">admin@trucktrack.com</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Partner:</span>
                    <code className="text-blue-100 bg-blue-600/30 px-2 py-1 rounded text-xs">partner@trucktrack.com</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200">Password:</span>
                    <code className="text-blue-100 bg-blue-600/30 px-2 py-1 rounded text-xs">password123</code>
                  </div>
            </div>
          </div> */}

              <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-white">
                    Email Address
                  </label>
                  <div className="relative">
              <input 
                id="email" 
                type="email" 
                required 
                value={loginData.email} 
                onChange={(e) => setLoginData({...loginData, email: e.target.value})} 
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200" 
                      placeholder="Enter your email"
                disabled={loading}
              />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span className="text-blue-200">📧</span>
                    </div>
                  </div>
            </div>

            {/* Password input with toggle visibility */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-white">
                Password
              </label>
              <div className="relative">
                <input 
                  id="password" 
                  type={showPassword ? "text" : "password"}
                  required 
                  value={loginData.password} 
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})} 
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200" 
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-2">
                  <button
                    type="button"
                    tabIndex={-1}
                    className="focus:outline-none"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <span className="text-blue-200 text-lg select-none">
                      {showPassword ? "🙈" : "🔒"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading} 
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Signing in...</span>
                </div>
              ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Sign In</span>
                      <span>→</span>
                    </div>
              )}
            </button>
            
                <div className="text-center">
                  <p className="text-blue-200 text-sm">
                    Don't have an account?{' '}
                    <Link to="/register" className="text-emerald-300 hover:text-emerald-200 font-semibold transition-colors">
                      Create one here
                    </Link>
                  </p>
              </div>
              </form>
            </div>
            
            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-blue-200 text-xs">
                By signing in, you agree to our{' '}
                <a href="#" className="text-emerald-300 hover:text-emerald-200">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-emerald-300 hover:text-emerald-200">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
